import { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import {
  Transition,
  Dialog,
  TransitionChild,
  DialogPanel,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/solid";
import { Page } from "./types";
import ScrollToTop from "./ScrollToTop";
import Footer from "shared/Footer/Footer";
import SiteHeader from "containers/SiteHeader";
import useWindowSize from "hooks/useWindowResize";
import { useData } from "data/data-provider";

// Lazy load all page components for code splitting
const Page404 = lazy(() => import("containers/Page404/Page404"));
const ListingCarPage = lazy(
  () => import("containers/ListingCarPage/ListingCarPage"),
);
const CheckOutPage = lazy(() => import("containers/CheckOutPage/CheckOutPage"));
const PayPage = lazy(() => import("containers/PayPage/PayPage"));
const PageContact = lazy(() => import("containers/PageContact/PageContact"));
const PageAbout = lazy(() => import("containers/PageAbout/PageAbout"));
const PageSignUp = lazy(() => import("containers/PageSignUp/PageSignUp"));
const PageLogin = lazy(() => import("containers/PageLogin/PageLogin"));
const PageForgotPass = lazy(
  () => import("containers/PageForgotPass/PageForgotPass"),
);
const PageHome3 = lazy(() => import("containers/PageHome/PageHome3"));
const ListingCarDetailPage = lazy(
  () =>
    import("containers/ListingDetailPage/listing-car-detail/ListingCarDetailPage"),
);
const AvailableCarsPage = lazy(
  () => import("containers/AvailableCarsPage/AvailableCarsPage"),
);
const BookingDetailsPage = lazy(
  () => import("containers/BookingDetailsPage/BookingDetailsPage"),
);
const CarListingCarDetailPage = lazy(
  () => import("containers/CarListingDetailPage/CarListingDetailPage"),
);
const EarnWithUsPage = lazy(
  () => import("containers/EarnWithUs/EarnWithUsPage"),
);
const PrivacyPolicyPage = lazy(
  () => import("containers/PrivacyPolicyPage/PrivacyPolicyPage"),
);
const CancellationPolicyPage = lazy(
  () => import("containers/CancellationPolicyPage/CancellationPolicyPage"),
);
const DallasAttractionsPage = lazy(
  () => import("containers/DallasAttractionsPage/DallasAttractionsPage"),
);
const FortWorthAttractionsPage = lazy(
  () => import("containers/FortWorthAttractionsPage/FortWorthAttractionsPage"),
);
const FortWorthStockyardPage = lazy(
  () => import("containers/FortWorthStockyardPage/FortWorthStockyardPage"),
);
const DFWFood = lazy(() => import("containers/DWFFood&Dining/DWFFood&Dining"));
const TermsandConditions = lazy(
  () => import("components/Terms&Conditions/TermsandConditions"),
);
const PartnerFormPage = lazy(
  () => import("containers/PartnerVehiclePage/PartnerVehiclePage"),
);
const VehicleDetailPage = lazy(
  () => import("containers/VehicleDetailPage/VehicleDetailPage"),
);

export const pages: Page[] = [
  { path: "/", exact: true, component: PageHome3 },

  //
  { path: "/available-car", component: AvailableCarsPage },
  { path: "/our-fleet", component: ListingCarPage },
  { path: "/booking-details", component: BookingDetailsPage },
  { path: "/listing-car-detail", component: ListingCarDetailPage },

  //
  { path: "/checkout", component: CheckOutPage },
  { path: "/pay-done/:bookingId", component: PayPage },
  //
  { path: "/contact-us", component: PageContact },
  { path: "/about-us", component: PageAbout },

  { path: "/dallas-attractions", component: DallasAttractionsPage },
  { path: "/fort-worth-attractions", component: FortWorthAttractionsPage },
  { path: "/fort-worth-stockyards", component: FortWorthStockyardPage },
  { path: "/dfw-food-dining", component: DFWFood },
  { path: "/earn-with-us", component: EarnWithUsPage },
  { path: "/privacy-policy", component: PrivacyPolicyPage },
  { path: "/cancellation-policy", component: CancellationPolicyPage },
  { path: "/terms-of-service", component: TermsandConditions },
  { path: "/investment-form", component: PartnerFormPage },

  // Legacy URL - kept for backward compatibility with noindex
  { path: "/car-listing-detail", component: CarListingCarDetailPage },

  // New clean URL structure for vehicles
  { path: "/book/:slug", component: VehicleDetailPage },
];
export enum AuthPages {
  Login,
  Signup,
  ForgotPass,
}
const MyRoutes = () => {
  const location = useLocation();
  let { isAuthPanelOpen, setIsAuthPanelOpen } = useData();
  let [authPageName, setAuthPageName] = useState<AuthPages>(AuthPages.Login);
  let WIN_WIDTH = useWindowSize().width;
  if (typeof window !== "undefined") {
    WIN_WIDTH = WIN_WIDTH || window.innerWidth;
  }

  useEffect(() => {
    if (isAuthPanelOpen) {
      setAuthPageName(AuthPages.Login);
    }
  }, [isAuthPanelOpen]);

  return (
    <div>
      <ScrollToTop />
      {location.pathname !== "/" &&
        location.pathname !== "/our-fleet" &&
        location.pathname !== "/our-fleet/" &&
        location.pathname !== "/about-us" &&
        location.pathname !== "/contact-us" &&
        location.pathname !== "/earn-with-us" &&
        location.pathname !== "/fort-worth-attractions" &&
        location.pathname !== "/fort-worth-stockyards" &&
        location.pathname !== "/dfw-food-dining" &&
        location.pathname !== "/dallas-attractions" && <SiteHeader />}

      <Suspense
        fallback={
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-6000"></div>
          </div>
        }
      >
        <Routes>
          {pages.map(({ component, path }) => {
            const Component = component;
            return <Route key={path} element={<Component />} path={path} />;
          })}
          <Route element={<Page404 />} />
        </Routes>
      </Suspense>

      <Footer />
      <Transition appear show={isAuthPanelOpen}>
        <Dialog
          as="div"
          className="relative z-10 focus:outline-none"
          onClose={() => {
            setIsAuthPanelOpen(false);
          }}
        >
          <div className="fixed inset-0 z-1000 w-screen overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <TransitionChild
                enter="ease-out duration-300"
                enterFrom="opacity-0 transform-[scale(95%)]"
                enterTo="opacity-100 transform-[scale(100%)]"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 transform-[scale(100%)]"
                leaveTo="opacity-0 transform-[scale(95%)]"
              >
                <DialogPanel className="w-full max-w-md lg:h-[700px] lg:max-w-2xl bg-[#ffffffff] rounded-xl p-6 backdrop:blur-2xl border border-text-color shadow-2xl">
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setIsAuthPanelOpen(false);
                      }}
                      className="text-text-color"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center h-full">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-6000"></div>
                      </div>
                    }
                  >
                    {authPageName === AuthPages.Login ? (
                      <PageLogin setAuthPageName={setAuthPageName} />
                    ) : authPageName === AuthPages.ForgotPass ? (
                      <PageForgotPass setAuthPageName={setAuthPageName} />
                    ) : (
                      <PageSignUp setAuthPageName={setAuthPageName} />
                    )}
                  </Suspense>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default MyRoutes;
